import { Trans } from 'react-i18next'
import type { LiabilityOverviewViewModel } from './buildLiabilityOverviewModel'
import useI18n from '@/shared/hooks/usei18n'
import {
  formatVolumeForLiability,
  getLiabilityVolumeDisplayParts,
  type NetMassDisplayUnit
} from '@/shared/netMassDisplay/netMassDisplayUnit'

export type LiabilityOverviewTemplateProps = {
  model: LiabilityOverviewViewModel | null
  originCountryCode: string
  displayUnit?: NetMassDisplayUnit
}

const formulaOperatorClass = 'text-text-secondary'
const formulaLineClass = 'font-mono text-sm'

/**
 * Presentational liability overview (dialog body, print/PDF page, etc.).
 */
export default function LiabilityOverviewTemplate({
  model,
  originCountryCode,
  displayUnit = 'tonnes'
}: LiabilityOverviewTemplateProps) {
  const {
    t,
    numberFormatter2Decimals,
    numberFormatter3Decimals,
    currencyFormatter2Decimals,
    formatCbamTco2Display
  } = useI18n()

  const formatSpecificEmissions = (value: number | undefined | null) =>
    value !== undefined && value !== null ? numberFormatter3Decimals(value) : '-'

  const formatEmissionsTotal = (value: number | undefined | null) => formatCbamTco2Display(value)

  if (!model) {
    return (
      <div className="bg-surface-secondary border border-border rounded-xl p-6">
        {t('common:liabilityExplanationDialog.unavailable')}
      </div>
    )
  }

  const routeDescription = model.routeLetter
    ? t(`common:productionRoutes.${model.routeLetter}`, {
        defaultValue: '-'
      })
    : null

  const sectorLabel =
    model.sector != null && model.sector !== ''
      ? t(`common:aggregatedGoodCategories.${model.sector}`, { defaultValue: '-' })
      : '-'

  const isIndirectExcludedForSector = Boolean(
    model.sectorSubjectToIndirectExclusionRule && model.indirectEffectivelyZero
  )

  const { volumeAmount } = getLiabilityVolumeDisplayParts(
    model.volumeTonnes,
    displayUnit,
    numberFormatter3Decimals
  )
  const volumeUnitLabel = t(
    displayUnit === 'tonnes' ? 'common:units.mass.tonnes' : 'common:units.mass.kilograms'
  )
  /** Worked formulas always show net mass in tonnes (PM ENG-1546); narrative uses displayUnit. */
  const volumeMassDisplayFormula = formatVolumeForLiability(
    model.volumeTonnes,
    'tonnes',
    numberFormatter3Decimals
  )

  /** Avoid {{goodsName}} in Trans strings — react-i18next HTML-escapes interpolated values. */
  const transComponents = {
    strong: <strong />,
    goodsName: <span>{model.goodsName}</span>
  }

  return (
    <div className="space-y-8 pb-6">
      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.introTitle')}
        </h3>
        <p>
          <Trans
            i18nKey="common:liabilityExplanationDialog.introText"
            values={{
              liabilityEur: currencyFormatter2Decimals(model.liabilityEur),
              year: model.year,
              volumeAmount,
              volumeUnitLabel,
              cnCode: model.cnCode
            }}
            components={transComponents}
          />
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.keyInputsTitle')}
        </h3>
        <ul className="list-disc ml-6 space-y-1">
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.goods"
              values={{ cnCode: model.cnCode }}
              components={transComponents}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.sector"
              values={{ sectorLabel }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.volume"
              values={{ volumeAmount, volumeUnitLabel }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.year"
              values={{ year: model.year }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.direct"
              values={{ value: formatSpecificEmissions(model.specificDirectTco2PerT) }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.indirect"
              values={{ value: formatSpecificEmissions(model.specificIndirectTco2PerT) }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.benchmark"
              values={{ value: formatSpecificEmissions(model.cbamBenchmarkTco2PerT) }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.cbamFactor"
              values={{ value: numberFormatter3Decimals(model.cbamFactor) }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.cscf"
              values={{ value: `${numberFormatter3Decimals(model.cscfPercentage)}%` }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.keyInputs.certificatePrice"
              values={{ value: currencyFormatter2Decimals(model.certificatePriceEurPerT) }}
              components={{ strong: <strong /> }}
            />
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.certificatePriceTitle')}
        </h3>
        <p>{t('common:liabilityExplanationDialog.certificatePriceUsedHeading')}</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.certificatePriceUsedListItem"
              values={{
                value: currencyFormatter2Decimals(model.certificatePriceEurPerT),
                source: t(`common:liabilityExplanationDialog.priceSource.${model.priceSource}`)
              }}
              components={{ strong: <strong /> }}
            />
          </li>
        </ul>
        <p>{t('common:liabilityExplanationDialog.certificatePriceNote')}</p>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.emissionsTitle')}
        </h3>
        {model.isUsingDefaultEmissions ? (
          <div className="space-y-2">
            <p>{t('common:liabilityExplanationDialog.defaultValuesIntro')}</p>
            <ul className="list-disc ml-6 space-y-1">
              {model.defaultMarkupPercent != null ? (
                <li>
                  <Trans
                    i18nKey="common:liabilityExplanationDialog.defaultMarkup"
                    values={{
                      year: model.year,
                      percent: numberFormatter2Decimals(model.defaultMarkupPercent)
                    }}
                    components={{ strong: <strong /> }}
                  />
                </li>
              ) : null}
              <li>
                <Trans
                  i18nKey="common:liabilityExplanationDialog.defaultValuesSource"
                  values={{
                    source:
                      model.defaultValueSource === 'other-countries-and-territories'
                        ? t('common:liabilityExplanationDialog.otherCountriesAndTerritories')
                        : t(`common:countriesByIsoAlpha2.${originCountryCode}`)
                  }}
                  components={{ strong: <strong /> }}
                />
              </li>
            </ul>
            <p>
              <Trans
                i18nKey="common:liabilityExplanationDialog.defaultValuesCallout"
                components={{ em: <em className="italic" /> }}
              />
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p>{t('common:liabilityExplanationDialog.actualValuesIntro')}</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>
                <Trans
                  i18nKey="common:liabilityExplanationDialog.actualValuesSource"
                  values={{
                    source:
                      model.actualValueSource || t('common:liabilityExplanationDialog.notAvailable')
                  }}
                  components={{ strong: <strong /> }}
                />
              </li>
            </ul>
            <p>
              <Trans
                i18nKey="common:liabilityExplanationDialog.actualValuesCallout"
                components={{ em: <em className="italic" /> }}
              />
            </p>
          </div>
        )}
      </section>

      {model.isUsingDefaultEmissions &&
      model.routeLetter &&
      routeDescription &&
      routeDescription !== '-' ? (
        <section className="space-y-2">
          <h3 className="text-lg font-semibold">
            {t('common:liabilityExplanationDialog.productionRouteTitle')}
          </h3>
          <p>
            <Trans
              i18nKey="common:liabilityExplanationDialog.productionRouteIntro"
              components={{ strong: <strong /> }}
            />
          </p>
          <ul className="list-disc ml-6">
            <li>
              <Trans
                i18nKey="common:liabilityExplanationDialog.productionRoute"
                values={{ routeLetter: model.routeLetter }}
                components={{ strong: <strong /> }}
              />
              <span>{` — ${routeDescription}`}</span>
            </li>
          </ul>
          <p>
            <Trans
              i18nKey="common:liabilityExplanationDialog.productionRouteCallout"
              components={{ em: <em className="italic" /> }}
            />
          </p>
        </section>
      ) : null}

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.indirectTitle')}
        </h3>
        <p>
          {isIndirectExcludedForSector
            ? t('common:liabilityExplanationDialog.indirectSectorRule')
            : t('common:liabilityExplanationDialog.indirectIncluded')}
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.totalsTitle')}
        </h3>
        <ul className="list-disc ml-6 space-y-3">
          <li className="space-y-1">
            <p>
              <Trans
                i18nKey="common:liabilityExplanationDialog.totals.totalDirectTitle"
                components={{ strong: <strong /> }}
              />
            </p>
            <p className={formulaLineClass}>
              = {volumeMassDisplayFormula}
              <span className={formulaOperatorClass}> × </span>
              {formatSpecificEmissions(model.specificDirectTco2PerT)} tCO₂e/t
            </p>
            <p className={formulaLineClass}>
              = <strong>{formatEmissionsTotal(model.totalDirectTco2)}</strong>
            </p>
          </li>
          {!isIndirectExcludedForSector ? (
            <li className="space-y-1">
              <p>
                <Trans
                  i18nKey="common:liabilityExplanationDialog.totals.totalIndirectTitle"
                  components={{ strong: <strong /> }}
                />
              </p>
              <p className={formulaLineClass}>
                = {volumeMassDisplayFormula}
                <span className={formulaOperatorClass}> × </span>
                {formatSpecificEmissions(model.specificIndirectTco2PerT)} tCO₂e/t
              </p>
              <p className={formulaLineClass}>
                = <strong>{formatEmissionsTotal(model.totalIndirectTco2)}</strong>
              </p>
            </li>
          ) : null}
          <li className="space-y-1">
            <p>
              <Trans
                i18nKey="common:liabilityExplanationDialog.totals.totalEmbeddedTitle"
                components={{ strong: <strong /> }}
              />
            </p>
            <p className={formulaLineClass}>
              = <strong>{formatEmissionsTotal(model.totalEmbeddedTco2)}</strong>
            </p>
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.benchmarkSectionTitle')}
        </h3>
        <p>{t('common:liabilityExplanationDialog.benchmarkIntro')}</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.benchmarkInputs.cnCode"
              values={{ value: model.cnCode }}
              components={{ strong: <strong /> }}
            />
          </li>
          {model.routeLetter && routeDescription && routeDescription !== '-' ? (
            <li>
              <Trans
                i18nKey="common:liabilityExplanationDialog.benchmarkInputs.route"
                values={{ routeLetter: model.routeLetter }}
                components={{ strong: <strong /> }}
              />
              <span>{` — ${routeDescription}`}</span>
            </li>
          ) : null}
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.benchmarkInputs.year"
              values={{ year: model.year }}
              components={{ strong: <strong /> }}
            />
          </li>
        </ul>
        <p>
          <Trans
            i18nKey="common:liabilityExplanationDialog.benchmarkYearCallout"
            values={{ year: model.year }}
            components={{ em: <em className="italic" /> }}
          />
        </p>

        <h4 className="text-base font-semibold">
          {t('common:liabilityExplanationDialog.benchmarkFactorTitle')}
        </h4>
        <ul className="list-disc ml-6">
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.benchmarkFactorValue"
              values={{ year: model.year, value: numberFormatter3Decimals(model.cbamFactor) }}
              components={{ strong: <strong /> }}
            />
          </li>
        </ul>
        <p className={formulaLineClass}>
          {t('common:liabilityExplanationDialog.benchmarkFactorFormula')}
        </p>
        <p className={formulaLineClass}>
          = {formatSpecificEmissions(model.cbamBenchmarkTco2PerT)} tCO₂e/t
          <span className={formulaOperatorClass}> × </span>
          {numberFormatter3Decimals(model.cbamFactor)}
        </p>
        <p className={formulaLineClass}>
          = <strong>{formatSpecificEmissions(model.intermediateBenchmarkPerT)} tCO₂e/t</strong>
        </p>

        <h4 className="text-base font-semibold">
          {t('common:liabilityExplanationDialog.benchmarkCscfTitle')}
        </h4>
        <ul className="list-disc ml-6">
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.benchmarkCscfValue"
              values={{ value: `${numberFormatter3Decimals(model.cscfPercentage)}%` }}
              components={{ strong: <strong /> }}
            />
          </li>
        </ul>
        <p className={formulaLineClass}>
          {t('common:liabilityExplanationDialog.benchmarkCscfFormula')}
        </p>
        <p className={formulaLineClass}>
          = {formatSpecificEmissions(model.cbamBenchmarkTco2PerT)} tCO₂e/t
          <span className={formulaOperatorClass}> × </span>
          {numberFormatter3Decimals(model.cbamFactor)}
          <span className={formulaOperatorClass}> × </span>
          {numberFormatter3Decimals(model.cscfPercentage)}%
        </p>
        <p className={formulaLineClass}>
          = <strong>{formatSpecificEmissions(model.discountPerTonneTco2)} tCO₂e/t</strong>
        </p>
        {Math.abs(model.cscfPercentage - 100) < 1e-9 ? (
          <p>
            <Trans
              i18nKey="common:liabilityExplanationDialog.benchmarkCscfCallout"
              components={{ em: <em className="italic" /> }}
            />
          </p>
        ) : null}

        <h4 className="text-base font-semibold">
          {t('common:liabilityExplanationDialog.benchmarkTotalDeductionTitle')}
        </h4>
        <ul className="list-disc ml-6 space-y-1">
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.benchmarkPerTonneDeduction"
              values={{ value: formatSpecificEmissions(model.discountPerTonneTco2) }}
              components={{ strong: <strong /> }}
            />
          </li>
          <li className="space-y-1">
            <p>
              <Trans
                i18nKey="common:liabilityExplanationDialog.benchmarkTotalDeduction"
                components={{ strong: <strong /> }}
              />
            </p>
            <p className={formulaLineClass}>
              = {formatSpecificEmissions(model.discountPerTonneTco2)} tCO₂e/t
              <span className={formulaOperatorClass}> × </span>
              {volumeMassDisplayFormula}
            </p>
            <p className={formulaLineClass}>
              = <strong>{formatEmissionsTotal(model.totalDiscountTco2)}</strong>
            </p>
          </li>
        </ul>
        {model.benchmarkDisclaimer ? (
          <p className="text-sm text-text-secondary whitespace-pre-wrap">
            {model.benchmarkDisclaimer}
          </p>
        ) : null}
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.certificatesTitle')}
        </h3>
        <p className={formulaLineClass}>
          {t('common:liabilityExplanationDialog.certificatesFormula')}
        </p>
        <p className={formulaLineClass}>
          = max(
          {formatEmissionsTotal(model.totalEmbeddedTco2)}
          <span className={formulaOperatorClass}> − </span>
          {formatEmissionsTotal(model.totalDiscountTco2)}, 0)
        </p>
        <p className={formulaLineClass}>
          = <strong>{formatEmissionsTotal(model.netEmissionsTco2)}</strong>
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.calculationTitle')}
        </h3>
        <p className={formulaLineClass}>
          {t('common:liabilityExplanationDialog.calculationFormula')}
        </p>
        <p className={formulaLineClass}>
          = {formatEmissionsTotal(model.netEmissionsTco2)}
          <span className={formulaOperatorClass}> × </span>
          {currencyFormatter2Decimals(model.certificatePriceEurPerT)} /t CO₂
        </p>
        <p className={formulaLineClass}>
          = <strong>{currencyFormatter2Decimals(model.liabilityEur)}</strong>
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t('common:liabilityExplanationDialog.finalResultTitle')}
        </h3>
        <p>
          {t('common:liabilityExplanationDialog.finalResultIntro', {
            volumeAmount,
            volumeUnitLabel,
            cnCode: model.cnCode,
            year: model.year
          })}
        </p>
        <ul className="list-disc ml-6 space-y-1">
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.finalResult.totalEmbedded"
              components={{ strong: <strong /> }}
            />{' '}
            {formatEmissionsTotal(model.totalEmbeddedTco2)}
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.finalResult.totalDiscount"
              components={{ strong: <strong /> }}
            />{' '}
            {formatEmissionsTotal(model.totalDiscountTco2)}
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.finalResult.netEmissions"
              components={{ strong: <strong /> }}
            />{' '}
            {formatEmissionsTotal(model.netEmissionsTco2)}{' '}
            <span className="text-text-secondary">
              {t('common:liabilityExplanationDialog.finalResult.atCertificatePrice', {
                price: currencyFormatter2Decimals(model.certificatePriceEurPerT)
              })}
            </span>
          </li>
          <li>
            <Trans
              i18nKey="common:liabilityExplanationDialog.finalResult.liability"
              components={{ strong: <strong /> }}
            />{' '}
            <strong>{currencyFormatter2Decimals(model.liabilityEur)}</strong>
          </li>
        </ul>
      </section>
    </div>
  )
}
